import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Question } from '../api/live';
import { cleanQuestionText } from '../lib/live-qa';

/**
 * Shared live Q&A panel for both the viewer and the presenter. It is purely
 * presentational: it renders the (already server-ordered) question list and a
 * floating toggle, and emits intent events the host wires to the API.
 *
 * Events (all CustomEvent, bubbling + composed):
 *   - 'qa-post'     detail { text: string }                      (viewer mode)
 *   - 'qa-vote'     detail { id: string }                        (viewer mode)
 *   - 'qa-moderate' detail { id: string; status: 'answered'|'hidden' } (presenter)
 *   - 'qa-toggle'   detail { open: boolean }
 */
@customElement('live-qa-panel')
export class LiveQaPanel extends LitElement {
  @property({ type: String }) mode: 'viewer' | 'presenter' = 'viewer';
  @property({ attribute: false }) questions: Question[] = [];
  @property({ attribute: false }) votedQids: Set<string> = new Set();
  @property({ type: Boolean }) open = false;

  @state() private draft = '';

  static styles = css`
    :host { position: fixed; right: 12px; bottom: 12px; z-index: 20; font-size: 13px; }
    .toggle {
      display: inline-flex; align-items: center; gap: 6px; padding: 8px 12px;
      border-radius: 999px; border: 1px solid var(--border-subtle, rgba(255,255,255,0.2));
      background: var(--surface, rgba(0,0,0,0.7)); color: var(--text-primary, #fff);
      cursor: pointer; box-shadow: 0 2px 10px rgba(0,0,0,0.25);
    }
    .panel {
      width: min(360px, 90vw); max-height: min(60vh, 520px); display: flex; flex-direction: column;
      margin-top: 8px; border-radius: 12px; overflow: hidden;
      background: var(--surface, #16181d); color: var(--text-primary, #fff);
      border: 1px solid var(--border-subtle, rgba(255,255,255,0.12));
      box-shadow: 0 8px 30px rgba(0,0,0,0.4);
    }
    .head { padding: 10px 12px; font-weight: 600; border-bottom: 1px solid var(--border-subtle, rgba(255,255,255,0.1)); display: flex; justify-content: space-between; align-items: center; }
    .head button { background: none; border: 0; color: inherit; cursor: pointer; font-size: 16px; line-height: 1; }
    .list { overflow-y: auto; padding: 8px; display: flex; flex-direction: column; gap: 8px; }
    .empty { color: var(--text-secondary, #98a2b3); padding: 16px; text-align: center; }
    .q { display: flex; gap: 8px; align-items: flex-start; padding: 8px; border-radius: 8px; background: var(--canvas, rgba(255,255,255,0.04)); }
    .q.answered { opacity: 0.6; }
    .q .body { flex: 1; min-width: 0; }
    .q .who { color: var(--text-secondary, #98a2b3); font-size: 11px; margin-bottom: 2px; }
    .q .who .badge { color: var(--success, #34d399); margin-left: 6px; }
    .q .text { word-wrap: break-word; }
    .vote { display: flex; flex-direction: column; align-items: center; min-width: 34px; padding: 2px 4px; border-radius: 8px; border: 1px solid var(--border-subtle, rgba(255,255,255,0.2)); background: none; color: inherit; cursor: pointer; }
    .vote[disabled] { opacity: 0.5; cursor: default; }
    .vote .n { font-weight: 600; }
    .mod { display: flex; gap: 6px; }
    .mod button { font-size: 11px; padding: 3px 6px; border-radius: 6px; border: 1px solid var(--border-subtle, rgba(255,255,255,0.2)); background: none; color: inherit; cursor: pointer; }
    form { display: flex; gap: 6px; padding: 8px; border-top: 1px solid var(--border-subtle, rgba(255,255,255,0.1)); }
    textarea { flex: 1; resize: none; min-height: 38px; max-height: 96px; border-radius: 8px; padding: 6px 8px; border: 1px solid var(--border-subtle, rgba(255,255,255,0.2)); background: var(--canvas, rgba(255,255,255,0.04)); color: inherit; font: inherit; }
    form button { padding: 0 12px; border-radius: 8px; border: 0; background: var(--accent, #4f7cff); color: #fff; cursor: pointer; }
    form button[disabled] { opacity: 0.5; cursor: default; }
    /* Short landscape (phone held sideways): cap the panel so it doesn't eat the
       slide; coarse pointers get safe-area insets + a ≥44px vote target. */
    @media (orientation: landscape) and (max-height: 540px) {
      .panel { max-height: min(72dvh, 360px); }
    }
    @media (pointer: coarse) {
      :host { bottom: calc(12px + env(safe-area-inset-bottom, 0px)); right: calc(12px + env(safe-area-inset-right, 0px)); }
      .vote { min-width: 44px; padding: 6px 8px; }
    }
  `;

  private emit(type: string, detail: unknown) {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }

  private toggle() {
    this.emit('qa-toggle', { open: !this.open });
  }

  private submit(e: Event) {
    e.preventDefault();
    const text = cleanQuestionText(this.draft);
    if (!text) return;
    this.emit('qa-post', { text });
    this.draft = '';
  }

  private renderQuestion(q: Question): TemplateResult {
    const voted = this.votedQids.has(q.id);
    return html`
      <div class="q ${q.status === 'answered' ? 'answered' : ''}">
        ${this.mode === 'viewer'
          ? html`<button class="vote" ?disabled=${voted} title=${voted ? 'You upvoted this' : 'Upvote'}
                          @click=${() => this.emit('qa-vote', { id: q.id })}>
                    <span>▲</span><span class="n">${q.votes}</span>
                  </button>`
          : html`<div class="vote" title="Upvotes"><span>▲</span><span class="n">${q.votes}</span></div>`}
        <div class="body">
          <div class="who">${q.name}${q.status === 'answered' ? html`<span class="badge">✓ answered</span>` : nothing}</div>
          <div class="text">${q.text}</div>
          ${this.mode === 'presenter'
            ? html`<div class="mod">
                     <button @click=${() => this.emit('qa-moderate', { id: q.id, status: 'answered' })}>✓ Answer</button>
                     <button @click=${() => this.emit('qa-moderate', { id: q.id, status: 'hidden' })}>Hide</button>
                   </div>`
            : nothing}
        </div>
      </div>`;
  }

  render(): TemplateResult {
    const count = this.questions.length;
    if (!this.open) {
      return html`<button class="toggle" @click=${this.toggle}>💬 Questions${count ? html` (${count})` : nothing}</button>`;
    }
    return html`
      <button class="toggle" @click=${this.toggle}>💬 Questions${count ? html` (${count})` : nothing}</button>
      <div class="panel">
        <div class="head"><span>Questions${count ? html` · ${count}` : nothing}</span><button @click=${this.toggle} title="Close">×</button></div>
        <div class="list">
          ${count === 0 ? html`<div class="empty">No questions yet.${this.mode === 'viewer' ? ' Be the first to ask.' : ''}</div>` : nothing}
          ${this.questions.map((q) => this.renderQuestion(q))}
        </div>
        ${this.mode === 'viewer'
          ? html`<form @submit=${this.submit}>
                   <textarea
                     placeholder="Ask a question…"
                     .value=${this.draft}
                     @input=${(e: Event) => (this.draft = (e.target as HTMLTextAreaElement).value)}
                   ></textarea>
                   <button type="submit" ?disabled=${cleanQuestionText(this.draft) === null}>Send</button>
                 </form>`
          : nothing}
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'live-qa-panel': LiveQaPanel;
  }
}
